import { Injectable, Optional } from "@angular/core";
import { BaseBlotterDataAdapter } from "./BaseBlotterDataAdapter";
import { DetailFillTableDefinitionFactory } from "../../model/DetailFillTableDefinitionFactory";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CurrencyPairCalendarPrimaryLookupService } from "../../lookup/model/impl/CurrencyPairCalendarPrimaryLookupService";
import { OrderDataLookupService } from "../../lookup/model/impl/OrderDataLookupService";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { Roles } from "../../model/core/Roles";
import { SysFeatures } from "../../model/core/SysFeatures";
import { SystemConfigPromiseService } from "shared/auth-services";
import { CcyPairLookupService } from "../../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../../lookup/model/impl/ClientLookupService";
import { TraderGroupLookupService } from "../../lookup/model/impl/TraderGroupLookupService";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { DealtPricingService } from "shared/cubiq-services/lookup/pricing/impl/DealtPricingService";
import { OrderSlippageService } from "../impl/OrderSlippageService";
import { GammaUtils, RungPosition } from "app/bfx-blotter-orders/GammaUtils";
import { DetailFillOrderData } from "./DetailFillOrderData";

@Injectable()
export class DetailFillOrderAdapter extends BaseBlotterDataAdapter {
  private defaultBookId: string;
  private defaultBookName: string;
  private defaultBookBranch: string;
  private fwdPricingRollService: boolean = false;

  constructor(
    protected _tableDefinitionFactory: DetailFillTableDefinitionFactory,
    private sysConfigService: SystemConfigPromiseService,
    private orderDataLookup: OrderDataLookupService,
    private ccyPairLookup: CcyPairLookupService,
    private clientLookup: ClientLookupService,
    private tgLookup: TraderGroupLookupService,
    private pricingService: DealtPricingService,
    private orderSlippageService: OrderSlippageService,
    @Optional() protected activeUserService: ActiveUserProfilePromiseService,
    private calendarService: CurrencyPairCalendarPrimaryLookupService,
  ) {
    super(_tableDefinitionFactory);
    activeUserService.getActiveUserData().then((user) => {
      this.defaultBookId = user.defaultPositionBookId;
      this.defaultBookName = user.defaultPositionBookName;
      this.defaultBookBranch = user.branchCd;
    });
    activeUserService.getUserUpdateSubject().subscribe((user) => {
      this.defaultBookId = user.defaultPositionBookId;
      this.defaultBookName = user.defaultPositionBookName;
      this.defaultBookBranch = user.branchCd;
    });
    this.sysConfigService.getSystemConfig(SysFeatures.forwardPricingType).then((value) => {
      this.fwdPricingRollService = value == "ROLLSERVICE";
    });
  }

  public async adaptOrderFill(
    orderFillData: CubiqTS.OrderFillDataImpl,
    fillData: Array<CubiqTS.OrderFillDataImpl>,
  ): Promise<DetailFillOrderData> {
    if (orderFillData) {
      let order: CubiqTS.OrderDataImpl = this.orderDataLookup.getOrderData(orderFillData.orderId);
      if (order) {
        let canOverRideStp: boolean =
          this.sysConfigService.hardHasRole(Roles.ORDER_FILL_STP_OVERRIDE) &&
          CubiqTS.FillStpType.STP_ALL == orderFillData.stpType;
        let partFillEnabled: boolean = this.isPartFillEnabled(order);
        let pair = this.ccyPairLookup.getCcyPair(order.baseCode, order.nonBaseCode);
        let client = this.clientLookup.getItem(order.clientCode);
        let posBook = this.getPosBookToUse(order, orderFillData);
        let tg = this.tgLookup.getTraderGroup(order.deskId);
        let slippage: CubiqTS.BigNumber = new CubiqTS.BigNumber(0);
        let minPrecision: number = order.isInvertedOrderFg
          ? Math.max(
              pair.spotInvertDecimalsQty,
              pair.markupSptInvDecimals,
              pair.limitEntrySpotInvertDecimals,
            )
          : Math.max(pair.rateDecimalsQty, pair.markupSptDecimals, pair.limitEntrySpotDecimals);
        let pipPrecision: number = order.isInvertedOrderFg
          ? pair.spotInvertDecimalsQty
          : pair.rateDecimalsQty;

        if (slippage != null && slippage.comparedTo(0) != 0) {
          slippage = new CubiqTS.BigNumber("10").pow(pipPrecision).times(slippage);
        }

        let aboveMarket: boolean = this.pricingService.isAboveMarket(order);
        let forwardAsSpot: boolean =
          order.product === CubiqTS.Product.FWD &&
          (order.orderType === CubiqTS.OrderType.ALGO ||
            (this.fwdPricingRollService &&
              order.requestTrackingType === CubiqTS.RequestTrackingType.SPOT_RATE));

        // if we are a gamma rung - we should link to the "next closest to mid"
        // which there may not be - as we may be "closest to mid"
        // as we may need to select/unselect the "next closest to the mid"
        let isLowestGammaRung: boolean = false;
        let isHighestGammaRung: boolean = false;
        let nextGammaRungClosestToMarket: CubiqTS.OrderData = null;
        if (CubiqTS.StrategyType.GAMMA == order.strategyType) {
          // make 'rows' as OrderDatas (bit heavy as have to look em up to do it)
          let odRows: CubiqTS.OrderDataImpl[] = null;
          if (null != fillData) {
            odRows = [];
            fillData.forEach((indivRow) => {
              let fdRow: CubiqTS.OrderFillDataImpl = indivRow as CubiqTS.OrderFillDataImpl;
              if (fdRow) {
                let odRow: CubiqTS.OrderDataImpl = this.orderDataLookup.getOrderData(fdRow.orderId);
                odRows.push(odRow);
              }
            });
          }

          nextGammaRungClosestToMarket = GammaUtils.getNextRungClosestToMid(order);
          let rungPosition: RungPosition = GammaUtils.getRungPosition(order, odRows);
          isLowestGammaRung = RungPosition.LOWEST == rungPosition;
          isHighestGammaRung = RungPosition.HIGHEST == rungPosition;
        }

        let calendar = await this.calendarService.getCalendarWithHolidays(
          order.baseCode,
          order.nonBaseCode,
          order.product,
        );

        let data = {
          orderId: orderFillData.orderId,
          orderRef: order.orderRef,
          key: orderFillData.orderId,
          clientCode: order.clientCode,
          clientName: order.clientName,
          instrument: order.isInvertedOrderFg
            ? order.nonBaseCode + "/" + order.baseCode
            : order.baseCode + "/" + order.nonBaseCode,
          baseCode: order.baseCode, // needed for TenorValueDateAdaptor - seeing as we "lost" the object
          baseCcyCd: order.baseCode,
          nonBaseCode: order.nonBaseCode, // needed for TenorValueDateAdaptor - seeing as we "lost" the object
          nonBaseCcyCd: order.nonBaseCode,
          clientIntent: order.clientIntent,
          dealtCcy: order.fixedCode,
          remainingAmount: orderFillData.fillAmount,
          fillAmount: orderFillData.fillAmount
            ? new CubiqTS.BigNumber(orderFillData.fillAmount)
            : null, // this will be the editable value for part fills
          spotMarketRate: orderFillData.fillData.spotMarketRate,
          origSpotMarketRate: orderFillData.fillData.spotMarketRate,
          forwardMarketPoints: orderFillData.fillData.forwardMarketPoints,
          forwardMarketRate: orderFillData.fillData.marketAllIn,
          clientSpot: orderFillData.fillData.spotClientRate,
          forwardPoints: orderFillData.fillData.forwardClientPoints,
          allInClientRate: orderFillData.fillData.clientAllIn,
          markup: this.getMargin(order, orderFillData),
          requestedOrderType: order.requestedOrderType,
          product: order.product,
          requestTrackingType: order.requestTrackingType,
          settleDate: order.settleDate,
          settleTenor: order.settleTenor,
          adjustedSettleTenor: order.adjustedSettleTenor,
          stp: CubiqTS.FillStpType.STP_ALL == orderFillData.stpType,
          canOverRideStp: canOverRideStp,
          stpHold: orderFillData.stpHold, // this flag is the one set on order entry screen, it not whether order should be stp'd or not
          partFillEnabled: partFillEnabled,
          positionBookId: posBook ? posBook.id : null,
          positionBookName: posBook ? posBook.name : null,
          positionBookBranch: posBook ? posBook.branch : null,
          traderGroupBranch: tg ? tg.branchCode : null,
          entryDate: order.entryDts,
          clientComment: order.comments1,
          privateComment: order.comments2,
          orderState: order.orderState,
          deskId: order.deskId.toString(),
          reverseEngineerEnabled: order.isRateReverseEngineeringFg,
          invertedOrder: order.isInvertedOrderFg,
          minPrecision: minPrecision,
          pipPrecision: pipPrecision,
          algoStrategyName: order.algoStrategyName,
          aboveMarket: aboveMarket,
          orderType: order.orderType,
          isComponentLeg: false,
          executionInstruction: order.executionInstruction,
          executionType: order.executionType,
          initialSlippage: slippage,
          slippage: slippage,
          fixedAmount: order.orderQty,
          clientBuysBase: this.getClientBuysBase(order),
          oboClient: order.oboClient,
          isForwardAsSpot: forwardAsSpot,

          strategyType: order.strategyType,
          siblingRelationship: order.siblingRelationship,
          gammaRungId: order.gammaRungId,
          nextGammaRungClosestToMarket: nextGammaRungClosestToMarket,
          gammaTop: isHighestGammaRung,
          gammaBottom: isLowestGammaRung,
          gammaRung: null != order.gammaRungId,

          // for Trader override of calculated settleDate
          valueDate: null,
          valueDateRequested: false,
          valueDateInvalid: false,
          lockedValueDate: null,

          tradeDateRollDate: calendar.tradeDateRollDate,
          tradeDateRollTime: calendar.tradeDateRollTime,
        };

        // Fill forward algos as spots
        if (order.product == CubiqTS.Product.FWD && forwardAsSpot) {
          data.product = CubiqTS.Product.SPT;
          data.settleTenor = CubiqTS.Tenor.SPOT;
        }

        return data;
      }
    }
    logger.out.error(
      `${this.constructor.name} unable to find order for fill data ${orderFillData.orderId}`,
    );

    return null;
  }

  private getClientBuysBase(order: CubiqTS.OrderDataImpl): boolean {
    let clientBuysBase: boolean =
      (order.baseCode == order.fixedCode && order.clientIntent == CubiqTS.OrderIntent.BUY) ||
      (order.clientIntent == CubiqTS.OrderIntent.SELL && order.nonBaseCode == order.fixedCode);

    if (order.isInvertedOrderFg) {
      clientBuysBase = !clientBuysBase;
    }

    return clientBuysBase;
  }

  private getMargin(
    order: CubiqTS.OrderDataImpl,
    fillData: CubiqTS.OrderFillDataImpl,
  ): CubiqTS.BigNumber {
    let clientBuysBase: boolean = this.getClientBuysBase(order);
    let margin: CubiqTS.BigNumber = null;

    if (
      fillData &&
      fillData.fillData &&
      fillData.fillData.clientAllIn instanceof CubiqTS.BigNumber &&
      fillData.fillData.marketAllIn instanceof CubiqTS.BigNumber
    ) {
      if (clientBuysBase) {
        margin = fillData.fillData.clientAllIn.minus(fillData.fillData.marketAllIn);
      } else {
        margin = fillData.fillData.marketAllIn.minus(fillData.fillData.clientAllIn);
      }
    } else {
      margin = new CubiqTS.BigNumber(0);
    }

    return margin;
  }

  private getPosBookToUse(order: CubiqTS.OrderDataImpl, fillOrder: CubiqTS.OrderFillDataImpl): any {
    if (this.defaultBookId != undefined) {
      if (order.requestedOrderType == CubiqTS.RequestOrderType.CALL) {
        return {
          id: this.defaultBookId,
          name: "-",
          branch: this.defaultBookBranch,
        };
      }
      return {
        id: this.defaultBookId,
        name: this.defaultBookName,
        branch: this.defaultBookBranch,
      };
    }
    if (fillOrder.fillData.book) {
      if (order.requestedOrderType == CubiqTS.RequestOrderType.CALL) {
        return {
          id: fillOrder.fillData.book.positionBookId.toString(),
          name: "-",
          branch: fillOrder.fillData.book.positionBookBranch,
        };
      }
      return {
        id: fillOrder.fillData.book.positionBookId.toString(),
        name: fillOrder.fillData.book.positionBookName,
        branch: fillOrder.fillData.book.positionBookBranch,
      };
    }
    return null;
  }

  private isPartFillEnabled(order: CubiqTS.OrderDataImpl): boolean {
    let allowPartFill: boolean = CubiqTS.FillSupport.ALLOW_PARTIAL == order.executionInstruction;
    if (allowPartFill) {
      switch (order.requestedOrderType) {
        case CubiqTS.RequestOrderType.CALL:
          return false;
        case CubiqTS.RequestOrderType.SL_ASK:
        case CubiqTS.RequestOrderType.SL_ATRATE:
        case CubiqTS.RequestOrderType.SL_BID:
        case CubiqTS.RequestOrderType.TSL:
        case CubiqTS.RequestOrderType.TSL_ASK:
        case CubiqTS.RequestOrderType.TSL_BID:
          return this.sysConfigService.hardGetSystemConfig(SysFeatures.enableSLPartFill) == "Y";
        default:
          return true;
      }
    }
    return false;
  }
}
